import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { 
  MapPin, 
  Upload, 
  Check, 
  X, 
  Home,
  Users,
  Bed,
  Bath,
  DollarSign
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const CreateListingPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [images, setImages] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      propertyType: 'apartment',
      roomType: 'entire_place',
      bedrooms: 1,
      bathrooms: 1,
      maxGuests: 2,
      price: 100,
      rules: {
        checkIn: '3:00 PM',
        checkOut: '11:00 AM',
        smokingAllowed: false,
        petsAllowed: false,
        partiesAllowed: false
      }
    }
  });

  const propertyTypes = [
    { value: 'apartment', label: 'Apartment', icon: '🏢' },
    { value: 'house', label: 'House', icon: '🏠' },
    { value: 'condo', label: 'Condo', icon: '🏘️' },
    { value: 'villa', label: 'Villa', icon: '🏖️' },
    { value: 'studio', label: 'Studio', icon: '🏠' },
    { value: 'room', label: 'Room', icon: '🛏️' }
  ];

  const roomTypes = [
    { value: 'entire_place', label: 'Entire place', description: 'Guests have the whole place to themselves' },
    { value: 'private_room', label: 'Private room', description: 'Guests have their own room in a shared space' },
    { value: 'shared_room', label: 'Shared room', description: 'Guests sleep in a room or common area that may be shared' }
  ];

  const availableAmenities = [
    'WiFi', 'Kitchen', 'Washer', 'Dryer', 'Air conditioning', 
    'Heating', 'Parking', 'Pool', 'Hot tub', 'Gym', 
    'TV', 'Workspace', 'Fireplace', 'Balcony', 'Garden'
  ];

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    setImages(prev => [...prev, ...files].slice(0, 10)); // Max 10 images
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const toggleAmenity = (amenity) => {
    setSelectedAmenities(prev => 
      prev.includes(amenity) 
        ? prev.filter(a => a !== amenity)
        : [...prev, amenity]
    );
  };

  const onSubmit = async (data) => {
    if (images.length === 0) {
      toast.error('Please add at least one image');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();

      // Append all form fields
      Object.keys(data).forEach(key => {
        if (key === 'location') {
          Object.keys(data.location).forEach(locKey => {
            formData.append(`location.${locKey}`, data.location[locKey]);
          });
        } else if (key === 'rules') {
          Object.keys(data.rules).forEach(ruleKey => {
            formData.append(`rules.${ruleKey}`, data.rules[ruleKey]);
          });
        } else {
          formData.append(key, data[key]);
        }
      });

      // Append amenities
      formData.append('amenities', JSON.stringify(selectedAmenities));

      // Append images
      images.forEach((image) => {
        formData.append('images', image);
      });

      await axios.post('/listings', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Listing created successfully!');
      navigate('/host');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create listing';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, 4));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const steps = [
    { number: 1, title: 'Basic Info', description: 'Tell us about your place' },
    { number: 2, title: 'Location', description: 'Where is your property located?' },
    { number: 3, title: 'Details', description: 'Add photos and amenities' },
    { number: 4, title: 'Pricing', description: 'Set your price and rules' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  currentStep >= step.number 
                    ? 'bg-primary-600 border-primary-600 text-white' 
                    : 'border-gray-300 text-gray-500'
                }`}>
                  {currentStep > step.number ? <Check size={20} /> : step.number}
                </div>
                {index < steps.length - 1 && (
                  <div className={`w-16 h-1 mx-4 ${
                    currentStep > step.number ? 'bg-primary-600' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 text-center">
            <h2 className="text-2xl font-bold text-gray-900">{steps[currentStep - 1].title}</h2>
            <p className="text-gray-600">{steps[currentStep - 1].description}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl shadow-soft p-8">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Property Title
                </label>
                <input
                  {...register('title', { required: 'Title is required' })}
                  type="text"
                  placeholder="e.g., Beautiful Downtown Apartment"
                  className="input-field"
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  {...register('description', { required: 'Description is required' })}
                  rows={4}
                  placeholder="Describe your property..."
                  className="input-field"
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Property Type
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {propertyTypes.map(type => (
                    <label key={type.value} className="cursor-pointer">
                      <input
                        {...register('propertyType')}
                        type="radio"
                        value={type.value}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-lg text-center transition-colors ${
                        watch('propertyType') === type.value 
                          ? 'border-primary-500 bg-primary-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        <div className="text-2xl mb-2">{type.icon}</div>
                        <div className="font-medium">{type.label}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Room Type
                </label>
                <div className="space-y-3">
                  {roomTypes.map(type => (
                    <label key={type.value} className="cursor-pointer">
                      <input
                        {...register('roomType')}
                        type="radio"
                        value={type.value}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-lg transition-colors ${
                        watch('roomType') === type.value 
                          ? 'border-primary-500 bg-primary-50' 
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        <div className="font-medium">{type.label}</div>
                        <div className="text-sm text-gray-600">{type.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Users className="inline mr-1" size={16} />
                    Max Guests
                  </label>
                  <input
                    {...register('maxGuests', { required: true, min: 1 })}
                    type="number"
                    min="1"
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Bed className="inline mr-1" size={16} />
                    Bedrooms
                  </label>
                  <input
                    {...register('bedrooms', { required: true, min: 0 })}
                    type="number"
                    min="0"
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Bath className="inline mr-1" size={16} />
                    Bathrooms
                  </label>
                  <input
                    {...register('bathrooms', { required: true, min: 0.5, step: 0.5 })}
                    type="number"
                    min="0.5"
                    step="0.5"
                    className="input-field"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Location */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address
                </label>
                <input
                  {...register('location.address', { required: 'Address is required' })}
                  type="text"
                  placeholder="123 Main Street"
                  className="input-field"
                />
                {errors.location?.address && (
                  <p className="mt-1 text-sm text-red-600">{errors.location.address.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    {...register('location.city', { required: 'City is required' })}
                    type="text"
                    placeholder="New York"
                    className="input-field"
                  />
                  {errors.location?.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.location.city.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State/Province
                  </label>
                  <input
                    {...register('location.state', { required: 'State is required' })}
                    type="text"
                    placeholder="NY"
                    className="input-field"
                  />
                  {errors.location?.state && (
                    <p className="mt-1 text-sm text-red-600">{errors.location.state.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <input
                    {...register('location.country', { required: 'Country is required' })}
                    type="text"
                    placeholder="USA"
                    className="input-field"
                  />
                  {errors.location?.country && (
                    <p className="mt-1 text-sm text-red-600">{errors.location.country.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zip Code
                  </label>
                  <input
                    {...register('location.zipCode', { required: 'Zip code is required' })}
                    type="text"
                    placeholder="10001"
                    className="input-field"
                  />
                  {errors.location?.zipCode && (
                    <p className="mt-1 text-sm text-red-600">{errors.location.zipCode.message}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Property Photos
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                    <div className="flex text-sm text-gray-600">
                      <label className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500">
                        <span>Upload photos</span>
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="sr-only"
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, GIF up to 10MB each</p>
                  </div>
                </div>

                {images.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                    {images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Amenities */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Amenities
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {availableAmenities.map(amenity => (
                    <button
                      key={amenity}
                      type="button"
                      onClick={() => toggleAmenity(amenity)}
                      className={`p-3 border-2 rounded-lg text-left transition-colors ${
                        selectedAmenities.includes(amenity)
                          ? 'border-primary-500 bg-primary-50 text-primary-700'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      {amenity}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Pricing */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <DollarSign className="inline mr-1" size={16} />
                  Price per night (USD)
                </label>
                <input
                  {...register('price', { required: 'Price is required', min: 1 })}
                  type="number"
                  min="1"
                  step="1"
                  placeholder="100"
                  className="input-field"
                />
                {errors.price && (
                  <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                )}
              </div>

              {/* House Rules */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  House Rules
                </label>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Check-in time</label>
                    <input
                      {...register('rules.checkIn')}
                      type="text"
                      placeholder="3:00 PM"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Check-out time</label>
                    <input
                      {...register('rules.checkOut')}
                      type="text"
                      placeholder="11:00 AM"
                      className="input-field"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      {...register('rules.smokingAllowed')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Smoking allowed</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      {...register('rules.petsAllowed')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Pets allowed</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      {...register('rules.partiesAllowed')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Parties/events allowed</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-8 border-t border-gray-200">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>

            {currentStep < 4 ? (
              <button
                type="button"
                onClick={nextStep}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                ) : (
                  'Create Listing'
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateListingPage;
